import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { QueryCompositeFilterConstraint } from "firebase/firestore";
import { FirestoreService } from "../../../../shared/api/firebase/firestoreService";
import { FirestoreCollectionReference } from "../../../../shared/api/firebase/references";
import { MedicalPaOrder } from "../../../data-model/medicalPaOrdersModel";
import { MedicalPaOrdersAuthStatus } from "../../../enums/medicalPaOrdersAuthStatus";
import {
  createCompositeFilterForMedicalPaTables,
  createOrderByForMedicalPaTables,
} from "../../../utils/createCompositeFilterForTables";
import { logDataToConsole } from "../../../utils/customLogger";

interface MedicalPaOrdersState {
  authorizedMedicalPaOrders: MedicalPaOrder[] | null;
  loading: boolean;
  error: string | null;
  lastVisible: any;
  showinLineLoader: boolean;
  totalDocuments: number;
  currentlyLoadedCount: number;
}

const initialState: MedicalPaOrdersState = {
  authorizedMedicalPaOrders: null,
  loading: false,
  error: null,
  lastVisible: null,
  showinLineLoader: false,
  totalDocuments: 0,
  currentlyLoadedCount: 0,
};

const authorizedMedicalPaOrdersSlice = createSlice({
  name: "authorizedMedicalPaOrders",
  initialState,
  reducers: {
    setAuthorizedMedicalPaOrders: (
      state,
      action: PayloadAction<MedicalPaOrder[]>,
    ) => {
      state.authorizedMedicalPaOrders = action.payload;
      state.currentlyLoadedCount = action.payload.length;
      state.loading = false;
      state.error = null;
    },
    appendAuthorizedMedicalPaOrders: (
      state,
      action: PayloadAction<MedicalPaOrder[]>,
    ) => {
      state.authorizedMedicalPaOrders = state.authorizedMedicalPaOrders
        ? [...state.authorizedMedicalPaOrders, ...action.payload]
        : action.payload;
      state.currentlyLoadedCount = state.authorizedMedicalPaOrders.length;
      state.loading = false;
      state.error = null;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.authorizedMedicalPaOrders = null;
      state.loading = false;
    },
    setLastVisible: (state, action: PayloadAction<any>) => {
      state.lastVisible = action.payload;
    },
    setTotalDocuments: (state, action: PayloadAction<number>) => {
      state.totalDocuments = action.payload;
    },
    resetPagination: (state) => {
      state.totalDocuments = 0;
      state.lastVisible = null;
      state.currentlyLoadedCount = 0;
      state.authorizedMedicalPaOrders = null;
    },
    setinLineLoader: (state, action: PayloadAction<boolean>) => {
      state.showinLineLoader = action.payload;
    },
  },
});

export const {
  setAuthorizedMedicalPaOrders,
  appendAuthorizedMedicalPaOrders,
  setLoading,
  setError,
  setLastVisible,
  setTotalDocuments,
  resetPagination,
  setinLineLoader,
} = authorizedMedicalPaOrdersSlice.actions;

export const fetchTotalDocumentsCount =
  (
    facilityId: string,
    providerId: string,
    status: string,
    searchTerm?: string,
    isAdmin?: boolean,
  ) =>
  async (dispatch) => {
    try {
      const allDocs =
        await FirestoreService.getAllDocumentCountByCompositeFilter(
          FirestoreCollectionReference.medicalPaOrders(),
          createCompositeFilterForMedicalPaTables(
            facilityId,
            providerId,
            status,
            searchTerm,
            isAdmin,
          ) as QueryCompositeFilterConstraint,
          [createOrderByForMedicalPaTables()],
        );

      dispatch(setTotalDocuments(allDocs));
    } catch (error) {
      logDataToConsole("Error fetching total document count", error);
      dispatch(setTotalDocuments(0));
      return 0;
    }
  };

export const fetchAuthorizedMedicalPaOrdersAfterSearch =
  (
    facilityId: string,
    providerId: string,
    searchTerm?: string,
    isAdmin?: boolean,
  ) =>
  async (dispatch, getState) => {
    const { authorizedMedicalPaOrders } = getState();
    if (authorizedMedicalPaOrders.loading) return;

    dispatch(setLoading(true));
    dispatch(resetPagination());

    try {
      const data =
        await FirestoreService.fetchQueryCollectionByCompositeFilter<MedicalPaOrder>(
          FirestoreCollectionReference.medicalPaOrders(),
          createCompositeFilterForMedicalPaTables(
            facilityId,
            providerId,
            MedicalPaOrdersAuthStatus.AuthRequired,
            searchTerm,
            isAdmin,
          ) as QueryCompositeFilterConstraint,
          [createOrderByForMedicalPaTables()],
        );

      dispatch(setAuthorizedMedicalPaOrders(data));
      dispatch(
        fetchTotalDocumentsCount(
          facilityId,
          providerId,
          MedicalPaOrdersAuthStatus.AuthRequired,
          searchTerm,
          isAdmin ?? false,
        ),
      );
    } catch (error) {
      dispatch(setError(error as string));
    }
  };

export const fetchAuthorizedMedicalPaOrders =
  (
    facilityId: string,
    pageSize: number,
    providerId: string,
    isAdmin: boolean,
  ) =>
  async (dispatch, getState) => {
    const { authorizedMedicalPaOrders } = getState();
    if (authorizedMedicalPaOrders.loading) return;

    dispatch(setLoading(true));
    dispatch(resetPagination());

    try {
      FirestoreService.paginateQueryWithCompositeFilter<MedicalPaOrder>(
        FirestoreCollectionReference.medicalPaOrders(),
        pageSize,
        ({ docs, lastVisible }) => {
          dispatch(setAuthorizedMedicalPaOrders(docs));
          dispatch(setLastVisible(lastVisible));
          dispatch(
            fetchTotalDocumentsCount(
              facilityId,
              providerId || "",
              MedicalPaOrdersAuthStatus.AuthRequired,
              undefined,
              isAdmin,
            ),
          );
        },
        (error) => {
          dispatch(setError(error.message));
        },
        undefined,
        createCompositeFilterForMedicalPaTables(
          facilityId,
          providerId,
          MedicalPaOrdersAuthStatus.AuthRequired,
          undefined,
          isAdmin,
        ) as QueryCompositeFilterConstraint,
        [createOrderByForMedicalPaTables()],
      );
    } catch (error) {
      logDataToConsole("Error in fetchAuthorizedMedicalPaOrders", error);
      dispatch(setError(error as string));
    }
  };

export const fetchMoreAuthorizedMedicalPaOrders =
  (
    facilityId: string,
    pageSize: number,
    providerId: string,
    isAdmin?: boolean,
  ) =>
  async (dispatch, getState) => {
    const { authorizedMedicalPaOrders } = getState();

    if (
      authorizedMedicalPaOrders.loading ||
      authorizedMedicalPaOrders.showinLineLoader ||
      authorizedMedicalPaOrders.currentlyLoadedCount ===
        authorizedMedicalPaOrders.totalDocuments
    ) {
      return;
    }

    dispatch(setinLineLoader(true));

    try {
      const unsubscribe =
        FirestoreService.paginateQueryWithCompositeFilter<MedicalPaOrder>(
          FirestoreCollectionReference.medicalPaOrders(),
          pageSize,
          ({ docs, lastVisible }) => {
            dispatch(appendAuthorizedMedicalPaOrders(docs));
            dispatch(setLastVisible(lastVisible));
            dispatch(setinLineLoader(false));
          },
          (error) => {
            dispatch(setError(error.message));
            dispatch(setinLineLoader(false));
          },
          authorizedMedicalPaOrders.lastVisible,
          createCompositeFilterForMedicalPaTables(
            facilityId,
            providerId,
            MedicalPaOrdersAuthStatus.AuthRequired,
            undefined,
            isAdmin,
          ) as QueryCompositeFilterConstraint,
          [createOrderByForMedicalPaTables()],
        );

      return unsubscribe;
    } catch (error) {
      dispatch(setError(error as string));
      dispatch(setinLineLoader(false));
    }
  };

export const hasMoreDataToLoad = (state, endIndex: number) => {
  const { authorizedMedicalPaOrders } = state;

  return (
    authorizedMedicalPaOrders.currentlyLoadedCount <
      authorizedMedicalPaOrders.totalDocuments &&
    endIndex === authorizedMedicalPaOrders.currentlyLoadedCount - 1
  );
};

export default authorizedMedicalPaOrdersSlice.reducer;
